import { CARE_LEVELS, CARE_LEVEL_DESCRIPTORS, careLevelLabel } from '@iculogbook/shared';
import type { CareLevel } from '@iculogbook/shared';
import { useState } from 'react';

import { Button, ChipGroup, Dialog } from '../../components/index.ts';
import { InfoIcon } from '../../layout/icons.tsx';

const OPTIONS = CARE_LEVELS.map((level) => ({
  value: String(level),
  label: careLevelLabel(level),
}));

type CareLevelFieldProps = {
  value: CareLevel | null;
  onChange: (value: CareLevel | null) => void;
};

/**
 * The ICS level of critical care, 0–3 — how sick the patient was, as opposed
 * to what was wrong with them, which is the reasons/problems field.
 *
 * Chips rather than the `SegmentedControl` that `CapabilityLevelField` uses,
 * for two reasons. This field is **optional**, and a segmented control has no
 * way back out of a mistaken tap, whereas single-select chips deselect. And
 * the two fields sit near each other on the same form — one numbered 1–4 and
 * one numbered 0–3 — so giving them visibly different shapes is worth more
 * than the consistency of making them look alike.
 *
 * The info dialog carries the definitions for the same reason the capability
 * one does: a trainee deciding between level 2 and level 3 wants the wording,
 * not a number.
 */
export function CareLevelField({ value, onChange }: CareLevelFieldProps) {
  const [open, setOpen] = useState(false);

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-1">
        <span className="text-sm font-medium text-text">Level of care</span>
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="What the levels of care mean"
          className="grid size-touch place-items-center rounded-md text-muted hover:text-text"
        >
          <InfoIcon className="size-5" />
        </button>
      </div>

      <ChipGroup
        label="Level of care"
        options={OPTIONS}
        // String round-trip, because chip values are strings and 0 is a real
        // level: `value === null ? null : String(value)` must not collapse to
        // a falsy check anywhere along the way.
        value={value === null ? null : String(value)}
        onChange={(next) => {
          onChange(next === null ? null : (Number(next) as CareLevel));
        }}
      />

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title="Levels of care"
        description="The Intensive Care Society levels of adult critical care."
        footer={
          <Button variant="secondary" onClick={() => setOpen(false)}>
            Close
          </Button>
        }
      >
        <dl className="mt-2 flex flex-col gap-3">
          {CARE_LEVELS.map((level) => (
            <div key={level} className="flex gap-3">
              <dt className="w-5 shrink-0 font-strong text-accent">{level}</dt>
              <dd className="text-sm text-muted">{CARE_LEVEL_DESCRIPTORS[level]}</dd>
            </div>
          ))}
        </dl>
      </Dialog>
    </div>
  );
}
